/**
 * Permission helpers for the admin panel.
 *
 * The signed-in admin's permission names (`Permission.route_name`, e.g.
 * "rooms.index") are stored at login. An admin with no role is the owner
 * account and is flagged `is_super_admin`, which bypasses every check.
 *
 * This decides what the panel *shows*. The API does not check permissions, so
 * these rules are presentation only: any signed-in admin's token can still reach
 * every admin endpoint directly.
 */

const readJson = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch {
    return null;
  }
};

export const getPermissions = () => {
  const stored = readJson('permissions');
  return Array.isArray(stored) ? stored : [];
};

export const isSuperAdmin = () => readJson('admin')?.is_super_admin === true;

export const getRoleName = () => readJson('admin')?.role_name ?? null;

/**
 * True for a front-desk/operational "Staff" account — the mandatory Check In
 * gate (see components/CheckInGate) only applies to these. The owner account
 * (`is_super_admin`) and anyone on the built-in "Admin" role never need to
 * check in/out; roles have no dedicated type field, so this is a name match
 * on the role created by the Roles seeder/Team Management screen.
 */
export const requiresCheckIn = () => {
  if (isSuperAdmin()) return false;
  return String(getRoleName() || '').trim().toLowerCase() !== 'admin';
};

/** True when the current admin may use `routeName`. An empty name is public. */
export const hasPermission = (routeName) => {
  if (!routeName) return true;
  if (isSuperAdmin()) return true;
  return getPermissions().includes(routeName);
};

/** True when any of the given names is granted — for menus covering a group. */
export const hasAnyPermission = (...routeNames) => {
  const names = routeNames.flat().filter(Boolean);
  if (names.length === 0) return true;
  return names.some(hasPermission);
};

/**
 * Where a signed-in admin lands after login, or when visiting the bare
 * /admin (or legacy /dashboard) URL. Dashboard shows business-wide stats, so
 * it's restricted — a Staff role without `dashboard.view` goes straight to
 * the Booking Calendar instead of a Forbidden dashboard.
 */
export const getDefaultAdminRoute = () => {
  if (hasPermission('dashboard.view')) return '/admin/dashboard';
  if (hasPermission('bookings.calendar')) return '/admin/booking-calendar';
  return '/admin/dashboard';
};
